import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Upload, Loader, CheckCircle, AlertCircle, X,
  MapPin, Package, Truck, Clock
} from 'lucide-react';
import { freightApi } from '../config/freightApi';
import './AIFileDropZone.css';

const AIFileDropZone = ({ onOrdersCreated }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [filename, setFilename] = useState('');
  const [creating, setCreating] = useState(false);

  // Get Quote modal state
  const [quoteModalIdx, setQuoteModalIdx] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteResults, setQuoteResults] = useState([]);
  const [datRate, setDatRate] = useState(null);
  const [quoteSortBy, setQuoteSortBy] = useState('price');
  const [expandedBreakdown, setExpandedBreakdown] = useState(null);

  const fileInputRef = useRef(null);

  const getApiBase = () => process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
  const getAuthToken = () => localStorage.getItem('idToken') || localStorage.getItem('authToken') || '';

  const handleFile = useCallback(async (file) => {
    setError(null);
    setParsing(true);
    setShipments([]);
    setFilename(file.name);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${getApiBase()}/agent/parse-file`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
        body: formData
      });

      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        if (res.status === 504) {
          throw new Error('AI 解析超时，请尝试上传更小的文件，或将大文件拆分后重试');
        }
        throw new Error(`服务器错误 (${res.status})，请稍后重试`);
      }

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Parse failed');
      }

      setShipments(data.data.shipments || []);
    } catch (err) {
      console.error('File parse error:', err);
      setError(err.message);
    } finally {
      setParsing(false);
    }
  }, []);

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };
  const handleClick = () => fileInputRef.current?.click();
  const handleInputChange = (e) => {
    const file = e.target.files[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  // 支持直接粘贴：截图（图片）随时可贴；纯文本仅在焦点不在输入框时接管，避免干扰其它输入
  useEffect(() => {
    const onPaste = (e) => {
      if (parsing || shipments.length > 0) return;
      const cd = e.clipboardData;
      if (!cd) return;

      for (const it of cd.items || []) {
        if (it.kind === 'file' && it.type.startsWith('image/')) {
          const file = it.getAsFile();
          if (file) { e.preventDefault(); handleFile(file); return; }
        }
      }

      const ae = document.activeElement;
      const inEditable = ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable);
      if (inEditable) return;

      const text = cd.getData('text/plain');
      if (text && text.trim().length > 3) {
        e.preventDefault();
        const f = new File([text], 'pasted.txt', { type: 'text/plain' });
        handleFile(f);
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [parsing, shipments.length, handleFile]);

  const updateShipment = (idx, field, value) => {
    setShipments(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const updateItem = (shipIdx, itemIdx, field, value) => {
    setShipments(prev => prev.map((s, si) => {
      if (si !== shipIdx) return s;
      const newItems = s.items.map((item, ii) =>
        ii === itemIdx ? { ...item, [field]: field === 'stackable' || field === 'hazmat' ? value : (isNaN(Number(value)) ? value : Number(value)) } : item
      );
      return { ...s, items: newItems };
    }));
  };

  const removeShipment = (idx) => {
    setShipments(prev => prev.filter((_, i) => i !== idx));
  };

  const handleCreateOrders = async () => {
    if (shipments.length === 0) return;
    setCreating(true);
    try {
      const items = shipments.map(s => ({
        tracking_number: s.trackingNumber,
        product_name_en: s.cargoDescription,
        origin_city: s.originCity,
        origin_state: s.originState,
        origin_zip: s.originZip,
        destination_city: s.destinationCity,
        destination_state: s.destinationState,
        destination_zip: s.destinationZip,
        destination_country: 'US',
        inquiry_company: s.inquiryCompany || null,
        company_name: s.companyName || null,
        recipient_name: s.recipientName,
        phone: s.recipientPhone,
        email: s.recipientEmail || null,
        address: s.recipientAddress,
        address_type: s.destinationLocationType === 'residential' ? 'Residential' : 'Commercial',
        cargo_value: s.cargoValue,
        notes: s.notes,
        dimensions: s.items.map(item => ({
          length: item.length,
          width: item.width,
          height: item.height,
          pieces: item.pallets,
          weight: item.weight,
          freightClass: item.freightClass || '',
          volume: Math.round((item.length * item.width * item.height) / 1728 * 100) / 100
        })),
        total_pieces: s.items.reduce((sum, item) => sum + (item.pallets || 1), 0)
      }));

      const res = await fetch(`${getApiBase()}/agent/parse-and-create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items, sourceFile: filename, autoApprove: true })
      });

      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Create failed');

      setShipments([]);
      setFilename('');
      if (onOrdersCreated) onOrdersCreated(data.data.orders);
    } catch (err) {
      console.error('Create orders error:', err);
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  // Get Quote for a specific shipment (carrier quotes + DAT rate in parallel)
  const handleGetQuote = async (idx) => {
    const s = shipments[idx];
    setQuoteModalIdx(idx);
    setQuoteLoading(true);
    setQuoteResults([]);
    setDatRate(null);
    setExpandedBreakdown(null);

    const today = new Date().toISOString().split('T')[0];
    const quoteRequestData = {
      originCity: s.originCity || '',
      originState: s.originState || '',
      originZip: s.originZip || '',
      originCountry: 'US',
      originLocationType: 'commercial',
      destinationCity: s.destinationCity || '',
      destinationState: s.destinationState || '',
      destinationZip: s.destinationZip || '',
      destinationCountry: 'US',
      destinationLocationType: s.destinationLocationType || 'commercial',
      pickupDate: s.pickupDate || today,
      items: s.items.map(item => ({
        description: item.description || s.cargoDescription || 'Freight',
        weight: String(item.weight || 500),
        length: String(item.length || 48),
        width: String(item.width || 40),
        height: String(item.height || 48),
        pallets: String(item.pallets || 1),
        freightClass: item.freightClass || '70',
        stackable: item.stackable !== false,
        hazmat: item.hazmat || false
      })),
      pickupServices: [],
      deliveryServices: [],
      distanceMiles: null
    };

    // Fetch carrier quotes and DAT rate in parallel
    const [carrierResult, datResult] = await Promise.allSettled([
      freightApi.getAllLTLQuotes(quoteRequestData),
      (s.originZip && s.destinationZip) ? fetch(`${getApiBase()}/dat/rate-lookup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          originZip: s.originZip,
          destinationZip: s.destinationZip,
          equipmentType: 'V',
          weight: s.items.reduce((sum, item) => sum + (item.weight || 0), 0)
        })
      }).then(r => r.json()) : Promise.resolve(null)
    ]);

    if (carrierResult.status === 'fulfilled') {
      setQuoteResults(carrierResult.value || []);
    } else {
      console.error('Carrier quotes failed:', carrierResult.reason);
      setError(`获取报价失败: ${carrierResult.reason?.message}`);
    }

    if (datResult.status === 'fulfilled' && datResult.value?.success) {
      setDatRate(datResult.value.data);
    } else {
      console.warn('DAT rate unavailable:', datResult.status === 'rejected' ? datResult.reason : datResult.value);
    }

    setQuoteLoading(false);
  };

  const sortedQuoteResults = [...quoteResults].sort((a, b) => {
    if (quoteSortBy === 'price') return (a.price || 0) - (b.price || 0);
    if (quoteSortBy === 'time') {
      const dA = parseInt(String(a.transitDays || '').match(/\d+/)?.[0] || '999');
      const dB = parseInt(String(b.transitDays || '').match(/\d+/)?.[0] || '999');
      return dA - dB;
    }
    if (quoteSortBy === 'name') return (a.carrier || '').localeCompare(b.carrier || '');
    return 0;
  });

  const handleReset = () => {
    setShipments([]);
    setFilename('');
    setError(null);
  };

  // ====== RENDER ======
  return (
    <div className="ai-dropzone-wrapper">
      {/* Drop Zone */}
      {shipments.length === 0 && !parsing && (
        <div
          className={`ai-dropzone ${isDragging ? 'dragging' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.xlsx,.xls,.csv,.txt"
            onChange={handleInputChange}
            style={{ display: 'none' }}
          />
          <Upload size={32} className="dropzone-icon" />
          <div className="dropzone-title">AI 智能解析询价文件</div>
          <div className="dropzone-hint">
            拖拽 PDF / Excel / 图片 到此处，点击上传，或直接粘贴 (⌘V / Ctrl+V) 截图
          </div>
          <div className="dropzone-formats">
            支持格式: PDF, Excel (.xlsx/.xls), 图片 (PNG/JPG), CSV, TXT
          </div>
        </div>
      )}

      {/* Parsing spinner */}
      {parsing && (
        <div className="ai-parsing">
          <Loader size={28} className="spin" />
          <span>AI 正在解析 <strong>{filename}</strong> ...</span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="ai-error">
          <AlertCircle size={16} />
          <span>{error}</span>
          <button onClick={() => setError(null)}><X size={14} /></button>
        </div>
      )}

      {/* Parsed Results */}
      {shipments.length > 0 && (
        <div className="ai-results">
          <div className="ai-results-header">
            <div className="results-title">
              <CheckCircle size={18} />
              从 <strong>{filename}</strong> 解析出 {shipments.length} 条货物
            </div>
            <div className="results-actions">
              <button className="btn-create-all" onClick={handleCreateOrders} disabled={creating}>
                {creating ? <Loader size={14} className="spin" /> : <Truck size={14} />}
                {creating ? '创建中...' : '确认创建全部报价单'}
              </button>
              <button className="btn-reset" onClick={handleReset}>
                <X size={14} /> 清除
              </button>
            </div>
          </div>

          {shipments.map((s, idx) => (
            <div key={idx} className="ai-shipment-card">
              <div className="card-header">
                <span className="card-index">#{idx + 1}</span>
                {s.trackingNumber && <span className="card-tracking">{s.trackingNumber}</span>}
                {s.inquiryCompany && <span className="card-company">客户: {s.inquiryCompany}</span>}
                <span className="card-desc">{s.cargoDescription || '未知货物'}</span>
                <div className="card-header-actions">
                  <button
                    className="btn-get-quote"
                    onClick={() => handleGetQuote(idx)}
                    disabled={!s.destinationZip}
                    title={!s.destinationZip ? '需要目的地邮编' : '获取9家运输商报价'}
                  >
                    <Truck size={14} /> Get Quote
                  </button>
                  <button className="btn-remove-card" onClick={() => removeShipment(idx)}>
                    <X size={14} />
                  </button>
                </div>
              </div>

              <div className="card-body">
                {/* Route */}
                <div className="card-section">
                  <div className="section-label"><MapPin size={13} /> 路线</div>
                  <div className="field-row">
                    <div className="field-group">
                      <label>Origin City</label>
                      <input value={s.originCity || ''} onChange={e => updateShipment(idx, 'originCity', e.target.value)} placeholder="City" />
                    </div>
                    <div className="field-group narrow">
                      <label>State</label>
                      <input value={s.originState || ''} onChange={e => updateShipment(idx, 'originState', e.target.value)} placeholder="ST" />
                    </div>
                    <div className="field-group">
                      <label>Zip</label>
                      <input value={s.originZip || ''} onChange={e => updateShipment(idx, 'originZip', e.target.value)} placeholder="Zip" />
                    </div>
                    <span className="route-arrow">→</span>
                    <div className="field-group">
                      <label>Dest City</label>
                      <input value={s.destinationCity || ''} onChange={e => updateShipment(idx, 'destinationCity', e.target.value)} placeholder="City" />
                    </div>
                    <div className="field-group narrow">
                      <label>State</label>
                      <input value={s.destinationState || ''} onChange={e => updateShipment(idx, 'destinationState', e.target.value)} placeholder="ST" />
                    </div>
                    <div className="field-group">
                      <label>Zip</label>
                      <input value={s.destinationZip || ''} onChange={e => updateShipment(idx, 'destinationZip', e.target.value)} placeholder="Zip" />
                    </div>
                  </div>
                  <div className="field-row">
                    <div className="field-group">
                      <label>询价客户 (Company)</label>
                      <input value={s.inquiryCompany || ''} onChange={e => updateShipment(idx, 'inquiryCompany', e.target.value)} placeholder="谁来询价 / Customer（进 Company 列）" />
                    </div>
                    <div className="field-group">
                      <label>收货公司</label>
                      <input value={s.companyName || ''} onChange={e => updateShipment(idx, 'companyName', e.target.value)} placeholder="目的地收货公司" />
                    </div>
                    <div className="field-group">
                      <label>地址类型</label>
                      <select value={s.destinationLocationType || 'commercial'} onChange={e => updateShipment(idx, 'destinationLocationType', e.target.value)}>
                        <option value="commercial">Commercial</option>
                        <option value="residential">Residential</option>
                        <option value="limited_access">Limited Access</option>
                      </select>
                    </div>
                  </div>
                  <div className="field-row">
                    <div className="field-group">
                      <label>收件人 (人名)</label>
                      <input value={s.recipientName || ''} onChange={e => updateShipment(idx, 'recipientName', e.target.value)} placeholder="联系人姓名" />
                    </div>
                    <div className="field-group">
                      <label>电话</label>
                      <input value={s.recipientPhone || ''} onChange={e => updateShipment(idx, 'recipientPhone', e.target.value)} placeholder="Phone" />
                    </div>
                    <div className="field-group">
                      <label>邮箱</label>
                      <input value={s.recipientEmail || ''} onChange={e => updateShipment(idx, 'recipientEmail', e.target.value)} placeholder="Email" />
                    </div>
                  </div>
                </div>

                {/* Items */}
                <div className="card-section">
                  <div className="section-label"><Package size={13} /> 货物明细 ({s.items.length})</div>
                  <div className="ai-items-scroll">
                  {s.items.map((item, ii) => (
                    <div key={ii} className="item-row">
                      <div className="item-index">#{ii + 1}</div>
                      <div className="field-group">
                        <label>Weight (lbs)</label>
                        <input type="number" value={item.weight || ''} onChange={e => updateItem(idx, ii, 'weight', e.target.value)} />
                      </div>
                      <div className="field-group">
                        <label>L (in)</label>
                        <input type="number" value={item.length || ''} onChange={e => updateItem(idx, ii, 'length', e.target.value)} />
                      </div>
                      <div className="field-group">
                        <label>W (in)</label>
                        <input type="number" value={item.width || ''} onChange={e => updateItem(idx, ii, 'width', e.target.value)} />
                      </div>
                      <div className="field-group">
                        <label>H (in)</label>
                        <input type="number" value={item.height || ''} onChange={e => updateItem(idx, ii, 'height', e.target.value)} />
                      </div>
                      <div className="field-group narrow">
                        <label>Pallets</label>
                        <input type="number" value={item.pallets || ''} onChange={e => updateItem(idx, ii, 'pallets', e.target.value)} />
                      </div>
                      <div className="field-group narrow">
                        <label>Class</label>
                        <input value={item.freightClass || ''} onChange={e => updateItem(idx, ii, 'freightClass', e.target.value)} />
                      </div>
                    </div>
                  ))}
                  </div>
                </div>

                {/* Notes */}
                {s.notes && (
                  <div className="card-notes">
                    <span className="notes-label">备注:</span> {s.notes}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quote Modal */}
      {quoteModalIdx !== null && (
        <div className="quote-modal-overlay">
          <div className="quote-modal" onClick={e => e.stopPropagation()}>
            <div className="quote-modal-header">
              <h3>
                <Truck size={18} />
                报价结果 — {shipments[quoteModalIdx]?.cargoDescription || `#${quoteModalIdx + 1}`}
              </h3>
              <div className="quote-route-info">
                {shipments[quoteModalIdx]?.originZip || '?'} → {shipments[quoteModalIdx]?.destinationCity}, {shipments[quoteModalIdx]?.destinationState} {shipments[quoteModalIdx]?.destinationZip}
              </div>
              <button className="quote-modal-close" onClick={() => { setQuoteModalIdx(null); setQuoteResults([]); setDatRate(null); setExpandedBreakdown(null); }}>
                <X size={20} />
              </button>
            </div>

            <div className="quote-modal-body">
              {quoteLoading ? (
                <div className="quote-loading">
                  <Loader size={28} className="spin" />
                  <span>正在从 9 家运输商 + DAT 获取报价...</span>
                </div>
              ) : quoteResults.length > 0 ? (
                <>
                  {/* DAT Market Reference */}
                  {datRate && datRate.available && (
                    <div className="dat-rate-panel">
                      <div className="dat-rate-header">
                        <span className="dat-label">DAT Market Reference</span>
                        {datRate.mock && <span className="dat-mock-badge">Mock</span>}
                        {datRate.mileage && <span className="dat-mileage">{datRate.mileage} miles</span>}
                      </div>
                      <div className="dat-rate-grid">
                        <div className="dat-rate-item">
                          <span className="dat-rate-title">Spot Rate</span>
                          <span className="dat-rate-value">${datRate.spotRate?.toLocaleString() || 'N/A'}</span>
                        </div>
                        <div className="dat-rate-item">
                          <span className="dat-rate-title">Contract</span>
                          <span className="dat-rate-value">${datRate.contractRate?.toLocaleString() || 'N/A'}</span>
                        </div>
                        <div className="dat-rate-item">
                          <span className="dat-rate-title">Low</span>
                          <span className="dat-rate-value">${datRate.low?.toLocaleString() || 'N/A'}</span>
                        </div>
                        <div className="dat-rate-item">
                          <span className="dat-rate-title">High</span>
                          <span className="dat-rate-value">${datRate.high?.toLocaleString() || 'N/A'}</span>
                        </div>
                        {datRate.perMile && (
                          <div className="dat-rate-item">
                            <span className="dat-rate-title">Per Mile</span>
                            <span className="dat-rate-value">${datRate.perMile}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="quote-sort-bar">
                    <span>排序:</span>
                    <button className={quoteSortBy === 'price' ? 'active' : ''} onClick={() => setQuoteSortBy('price')}>最低价格</button>
                    <button className={quoteSortBy === 'time' ? 'active' : ''} onClick={() => setQuoteSortBy('time')}>最快到达</button>
                    <button className={quoteSortBy === 'name' ? 'active' : ''} onClick={() => setQuoteSortBy('name')}>A-Z</button>
                    <span className="quote-count">{sortedQuoteResults.length} 个报价</span>
                  </div>
                  <div className="quote-list">
                    {sortedQuoteResults.map(q => (
                      <div key={q.id} className="quote-item">
                        <div className="quote-row">
                          <div className="quote-carrier">
                            {q.logo && <img src={q.logo} alt={q.carrier} className="quote-carrier-logo" />}
                            <div>
                              <div className="quote-carrier-name">{q.carrier}</div>
                              <div className="quote-service-type">{q.serviceType}</div>
                            </div>
                          </div>
                          <div className="quote-price-col">
                            <div className="quote-price">${(q.price || 0).toFixed(2)}</div>
                            <button
                              className="btn-breakdown-toggle"
                              onClick={() => setExpandedBreakdown(expandedBreakdown === q.id ? null : q.id)}
                            >
                              {expandedBreakdown === q.id ? '收起明细' : '价格明细'}
                            </button>
                          </div>
                          <div className="quote-transit">
                            <Clock size={13} /> {q.transitDays || 'TBD'}
                          </div>
                          <div className="quote-extra">
                            {q.isGuaranteed && <span className="badge-guaranteed">Guaranteed</span>}
                            {q.maxLiability && (
                              <span className="badge-liability">
                                Liability: ${q.maxLiability.new?.toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                        {expandedBreakdown === q.id && (
                          <div className="quote-breakdown">
                            {q.charges && q.charges.length > 0 ? (
                              <>
                                {q.charges.map((c, ci) => (
                                  <div key={ci} className="breakdown-line">
                                    <span>{c.description}</span>
                                    <span>${parseFloat(c.amount || 0).toFixed(2)}</span>
                                  </div>
                                ))}
                                {q.fuelSurcharge && !q.charges.some(c => (c.description || '').toLowerCase().includes('fuel')) && (
                                  <div className="breakdown-line">
                                    <span>Fuel Surcharge</span>
                                    <span>${parseFloat(q.fuelSurcharge).toFixed(2)}</span>
                                  </div>
                                )}
                                <div className="breakdown-line breakdown-total">
                                  <span>Total</span>
                                  <span>${(q.price || 0).toFixed(2)}</span>
                                </div>
                              </>
                            ) : q.fuelSurcharge ? (
                              <>
                                <div className="breakdown-line">
                                  <span>Base Freight</span>
                                  <span>${(Math.round((q.price - parseFloat(q.fuelSurcharge)) * 100) / 100).toFixed(2)}</span>
                                </div>
                                <div className="breakdown-line">
                                  <span>Fuel Surcharge</span>
                                  <span>${parseFloat(q.fuelSurcharge).toFixed(2)}</span>
                                </div>
                                <div className="breakdown-line breakdown-total">
                                  <span>Total</span>
                                  <span>${(q.price || 0).toFixed(2)}</span>
                                </div>
                              </>
                            ) : (
                              <div className="breakdown-line breakdown-total">
                                <span>Total Charge</span>
                                <span>${(q.price || 0).toFixed(2)}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="quote-empty">未获取到报价，请检查地址和货物信息是否完整。</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIFileDropZone;
